import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, MessageCircle, ScanFace, Brain, Fingerprint, BadgeCheck, Lock,
  KeyRound, AlertTriangle, Clock, Zap, Nfc, Layers, Gauge, Smile, ShieldCheck, ShieldAlert, Timer,
} from "lucide-react";
import { Reveal } from "../../components/Reveal";
import { WA_NUMBER } from "../../data/site";

const HIGHLIGHTS = ["Real-Time Authentication", "Contactless Identity Verification", "AI-Powered Facial Recognition", "Enhanced Security"];

const CHALLENGES = [
  { icon: KeyRound, title: "Security Vulnerabilities", desc: "Passwords and physical credentials may be misused or compromised." },
  { icon: AlertTriangle, title: "Risk of Fraud", desc: "Traditional authentication methods can be vulnerable to unauthorized use." },
  { icon: Clock, title: "Time-Consuming Verification", desc: "Manual or credential-based verification can slow down access and authentication processes." },
];

const WORKFLOW = [
  { icon: ScanFace, step: "Face Scan", desc: "The system captures a facial image for authentication." },
  { icon: Brain, step: "AI Analysis", desc: "The facial recognition model analyzes the captured face." },
  { icon: Fingerprint, step: "Identity Matching", desc: "The system compares the facial data for authentication." },
  { icon: BadgeCheck, step: "Authentication", desc: "The individual is authenticated based on the recognition result." },
  { icon: Lock, step: "Secure Access", desc: "The authentication result can be used to support secure and contactless identity verification." },
];

const CAPABILITIES = [
  { icon: Zap, title: "Real-Time Authentication", desc: "Scans and authenticates individuals using facial recognition technology." },
  { icon: Nfc, title: "Contactless Verification", desc: "Enables authentication without requiring passwords or physical cards." },
  { icon: Brain, title: "AI-Powered Recognition", desc: "Uses deep learning-based facial recognition for identity authentication." },
  { icon: Layers, title: "Enhanced Security Layer", desc: "Adds an additional security layer for identity verification." },
  { icon: Gauge, title: "Fast Authentication", desc: "Supports quicker verification compared with manual authentication processes." },
  { icon: Smile, title: "Improved User Convenience", desc: "Provides a seamless and contactless authentication experience." },
];

const TECH = [
  { icon: Brain, title: "Deep Learning", desc: "Used for training AI models capable of facial recognition." },
  { icon: ScanFace, title: "Facial Recognition", desc: "Used to authenticate individuals based on facial scans." },
];

const IMPACT = [
  { icon: ShieldCheck, title: "Enhanced Security", desc: "Adds an additional layer of security and helps reduce unauthorized access." },
  { icon: ShieldAlert, title: "Fraud Prevention", desc: "Provides an authentication method designed to reduce dependence on passwords and physical credentials." },
  { icon: Smile, title: "Improved Convenience", desc: "Creates a seamless and contactless authentication experience." },
  { icon: Timer, title: "Faster Verification", desc: "Supports quick identity authentication in environments requiring efficient access verification." },
];

const BlockTitle = ({ kicker, children }) => (
  <Reveal>
    <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">{kicker}</p>
    <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-10">{children}</h2>
  </Reveal>
);

const HeroVisual = () => (
  <div className="relative rounded-3xl overflow-hidden border border-white/12 shadow-2xl shadow-black/50">
    <img src="/products/face-recognition-1.png" alt="AI facial recognition with identity mesh and verified attendance" className="w-full object-cover" />
    <motion.div
      initial={{ top: "-10%" }}
      animate={{ top: "110%" }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
      className="absolute left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-smipl-orange to-transparent opacity-80"
    />
    <div className="absolute top-4 left-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30 flex items-center gap-2">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-smipl-orange" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-smipl-orange" />
      </span>
      Live Verification
    </div>
  </div>
);

const FaceRecognitionPage = ({ openContact }) => (
  <main className="pt-36 pb-28 min-h-screen" data-testid="product-page-face-recognition">
    <div className="max-w-6xl mx-auto px-6">
      <Reveal>
        <Link to="/products" data-testid="face-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors">
          <ArrowLeft size={13} /> Our Products <span className="text-white/20">/</span>
          <span className="text-slate-400 normal-case tracking-normal">Face Recognition System</span>
        </Link>
      </Reveal>

      <div className="mt-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal delay={0.05}>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">Video Analytics & Surveillance AI</p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
              Face Recognition System
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">
              An AI-powered facial recognition solution that authenticates individuals by scanning and analyzing
              facial features, providing a fast, secure, and contactless method of identity verification.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {HIGHLIGHTS.map((h) => (
                <span key={h} className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-medium text-slate-200">
                  {h}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="face-talk-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
                Discuss Your Requirement <ArrowUpRight size={15} />
              </button>
              <Link to="/products" data-testid="face-back-btn" className="flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-smipl-orange hover:text-smipl-orange transition-all duration-300">
                <ArrowLeft size={15} /> Back to Products
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <HeroVisual />
        </Reveal>
      </div>

      <section className="mt-28" data-testid="face-challenge">
        <BlockTitle kicker="The Problem">The Challenge</BlockTitle>
        <Reveal delay={0.05}>
          <p className="max-w-3xl text-slate-400 text-base leading-relaxed mb-10">
            Traditional authentication methods such as passwords and ID cards can be vulnerable to fraud, misuse,
            and security breaches. They can also create inconvenience and delays, particularly in environments
            where fast and secure identity verification is required.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-5">
          {CHALLENGES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="tracing-border h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 border border-red-500/25 text-red-400">
                  <c.icon size={19} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="face-solution">
        <div className="rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/[0.08] to-transparent p-10 md:p-14">
          <BlockTitle kicker="The Solution">Secure Identity Verification Powered by AI</BlockTitle>
          <Reveal delay={0.08}>
            <div className="max-w-3xl space-y-4 text-slate-300 text-base leading-relaxed -mt-4">
              <p>The Face Recognition System uses AI-powered facial recognition to authenticate individuals through facial scanning.</p>
              <p>
                The system provides a secure and contactless approach to identity verification, reducing dependence on
                traditional passwords or physical ID cards. By enabling fast authentication through facial recognition
                technology, the solution adds an additional layer of security while improving user convenience.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mt-28" data-testid="face-workflow">
        <BlockTitle kicker="Workflow">How the System Works</BlockTitle>
        <div className="relative">
          <div className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-white/10" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-smipl-orange origin-left"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {WORKFLOW.map((w, i) => (
              <Reveal key={w.step} delay={i * 0.1}>
                <div data-testid={`face-step-${i + 1}`} className="relative rounded-2xl border border-white/8 bg-navy-deep/60 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-smipl-orange text-navy-deep font-mono text-xs font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <w.icon size={20} className="mt-5 text-smipl-orange" />
                  <h3 className="mt-3 font-display text-base font-bold">{w.step}</h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-28" data-testid="face-capabilities">
        <BlockTitle kicker="Capabilities">Key Capabilities</BlockTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.07}>
              <div className="tracing-border group h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange group-hover:bg-smipl-orange group-hover:text-white transition-all duration-300">
                  <c.icon size={19} />
                </div>
                <h3 className="mt-5 font-display text-base font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="face-technology">
        <BlockTitle kicker="Technology">Technology Behind the Solution</BlockTitle>
        <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
          {TECH.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <t.icon size={26} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-lg font-bold font-mono tracking-wide">{t.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="face-impact">
        <BlockTitle kicker="Business Impact">Smarter, Faster and More Secure Authentication</BlockTitle>
        <div className="grid sm:grid-cols-2 gap-5">
          {IMPACT.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <c.icon size={26} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-xl font-bold">{c.title}</h3>
                <p className="mt-2 text-sm md:text-base text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Reveal delay={0.1}>
        <div className="mt-28 rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/10 to-transparent p-10 md:p-14 text-center" data-testid="face-cta">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Ready to Strengthen Your <span className="text-smipl-orange">Identity Security?</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-slate-400 text-base leading-relaxed">
            Explore how AI-powered facial recognition can support secure, fast, and contactless identity
            authentication for your organization.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <button data-testid="face-experts-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-8 py-4 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
              Talk to Our Experts <ArrowUpRight size={15} />
            </button>
            <button
              data-testid="face-whatsapp-btn"
              onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
              className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-8 py-4 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300"
            >
              <MessageCircle size={15} /> WhatsApp
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  </main>
);

export default FaceRecognitionPage;
